import {format} from 'date-fns';
import {
  closeCurrentDialog,
  closeDialog,
  IconButton,
  openDialog,
  useTranslationContext,
} from 'platform/components';
import {Box, getSize, Heading, HStack, Space, Text} from 'platform/foundation';
import {convertStringLocaleToDateFns, useLocale} from 'platform/locale';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import {MonthEventItem} from './MonthEventItem';

interface MonthMoreEventsProps extends RequiredTestIdProps {
  date: Date;
  hiddenEvents: BigCalendarEvent[];
  onEventClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

/**
 * The "+N more" chip under a month day and the dialog listing the events that did not fit.
 */
export function MonthMoreEvents(props: MonthMoreEventsProps) {
  const {language} = useLocale();
  const t = useTranslationContext();

  const openMoreEventsDialog = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_month_cell_more_events_click',
      value: {
        date: props.date.toISOString(),
      },
    });
    openDialog(
      <Box padding={3}>
        <HStack align="center" justify="space-between">
          <Heading data-testid={suffixTestId('moreEvents-dialog-title', props)} size={5}>
            {format(props.date, 'd. LLLL yyyy, EEEE', {
              locale: convertStringLocaleToDateFns(language),
            })}
          </Heading>
          <IconButton
            data-testid={suffixTestId('moreEvents-dialog-close', props)}
            icon="navigation/close"
            priority="default"
            onClick={closeCurrentDialog}
          />
        </HStack>
        <Space vertical={3} />
        <div className="flex flex-col gap-[2px]">
          {props.hiddenEvents.map((event, eventIndex) => (
            <div key={event.id} style={{height: getSize(5)}}>
              <MonthEventItem
                data-testid={suffixTestId(`hiddenEvents-[${eventIndex}]`, props)}
                event={event}
                onClick={(clickedEvent) => {
                  props.onEventClick?.(clickedEvent);
                  closeDialog(MORE_EVENTS_DIALOG_ID);
                }}
                enableHover={props.enableHover}
                onMouseEnter={props.onEventMouseEnter}
                onMouseLeave={props.onEventMouseLeave}
              />
            </div>
          ))}
        </div>
      </Box>,
      {
        id: MORE_EVENTS_DIALOG_ID,
        'data-testid': suffixTestId('moreEvents-dialog', props),
        size: 'small',
        scrollBehavior: 'outside',
        disableBodyPadding: true,
      }
    );
  };

  return (
    <div
      data-testid={suffixTestId('moreEvents', props)}
      className="cursor-pointer overflow-hidden rounded-sm bg-[var(--color-palettes-neutral-30-100)] px-1 py-[2px] text-ellipsis whitespace-nowrap transition-colors duration-200 ease-in-out hover:bg-[var(--color-palettes-neutral-40-100)]"
      style={{height: getSize(5)}}
      onClick={openMoreEventsDialog}
    >
      <Text size="xSmall" color="secondary" noWrap>
        {t('bigCalendar.moreEvents', {count: props.hiddenEvents.length})}
      </Text>
    </div>
  );
}

const MORE_EVENTS_DIALOG_ID = 'monthCell-moreEvents-dialog';
